// Typed error classes for the socket layer. Callers can branch on the
// class (or `code` field) to decide whether to retry, surface to the user,
// or escalate to session-expired handling.
//
// Error message strings are deliberately stable — saga consumers and tests
// match on them (e.g. 'socket_disconnected', 'ack_timeout').

export class SocketError extends Error {
    constructor(message, code, retriable = false) {
        super(message);
        this.name = 'SocketError';
        this.code = code;
        this.retriable = retriable;
    }
}

// Socket is not currently connected. Transient — the saga can wait for
// reconnection and retry idempotent calls.
export class SocketDisconnectedError extends SocketError {
    constructor() {
        super('socket_disconnected', 'DISCONNECTED', true);
        this.name = 'SocketDisconnectedError';
    }
}

// Server didn't ack within the timeout window. May indicate slow network
// or a server-side handler that didn't call back. Transient.
export class SocketTimeoutError extends SocketError {
    constructor() {
        super('ack_timeout', 'TIMEOUT', true);
        this.name = 'SocketTimeoutError';
    }
}

// Server explicitly rejected the request via { ok: false, error: <code> }.
// Not retriable by default — the server said no, retrying won't change
// the answer. Wrap with retriable: true at the call site if the specific
// error code is known to be transient.
export class SocketServerError extends SocketError {
    constructor(message, code, retriable = false) {
        super(message || 'server_error', code || 'SERVER_ERROR', retriable);
        this.name = 'SocketServerError';
    }
}

// Auth failed (bad / expired token, banned account, etc.). Terminal —
// the calling saga should hand off to handleSessionExpired().
export class SocketAuthError extends SocketError {
    constructor() {
        super('authentication_failed', 'AUTH_FAILED', false);
        this.name = 'SocketAuthError';
    }
}

const AUTH_ERROR_CODES = new Set([
    'authentication_failed',
    'unauthorized',
    'session_expired',
    'token_expired',
]);

// Map a server-supplied error code into the right error class.
export function classifyServerError(code) {
    if (AUTH_ERROR_CODES.has(code)) return new SocketAuthError();
    return new SocketServerError(code, code, false);
}
